/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { Injectable } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatDateFormats, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

const MONDAY = 1;
const DAY_MONTH_YEAR = /^\s*(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})\s*$/;
const TWENTY_FIRST_CENTURY = 2000;
const EARLIEST_PLAUSIBLE_YEAR = 1900;

function isPlausibleCalendarDate(value: Date | null): boolean {
    return value instanceof Date && !isNaN(value.getTime()) && value.getFullYear() >= EARLIEST_PLAUSIBLE_YEAR;
}

/** True when the user typed something that is not a usable date - the caller should put the
 *  previous value back rather than clear it. An emptied input is a deliberate clear, not a rejection. */
export function typedDateWasRejected(change: MatDatepickerInputEvent<Date>): boolean {
    const typedText = (change.targetElement as HTMLInputElement).value.trim();
    return typedText !== '' && !isPlausibleCalendarDate(change.value);
}

@Injectable()
export class IrishNativeDateAdapter extends NativeDateAdapter {
    getFirstDayOfWeek(): number {
        return MONDAY;
    }

    parse(value: unknown, parseFormat?: unknown): Date | null {
        const dayMonthYear = typeof value === 'string' ? DAY_MONTH_YEAR.exec(value) : null;
        if (!dayMonthYear) {
            return super.parse(value, parseFormat);
        }
        const [, day, month, year] = dayMonthYear.map(Number);
        const fullYear = year < 100 ? TWENTY_FIRST_CENTURY + year : year;
        const parsed = new Date(fullYear, month - 1, day);
        const rolledOverIntoAnotherMonth = parsed.getMonth() !== month - 1;
        return rolledOverIntoAnotherMonth ? this.invalid() : parsed;
    }
}

export const IRISH_DATE_TIME_FORMATS: MatDateFormats = {
    parse: {
        dateInput: null,
        timeInput: null,
    },
    display: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' },
        timeInput: { hour: '2-digit', minute: '2-digit', hour12: false },
        timeOptionLabel: { hour: '2-digit', minute: '2-digit', hour12: false },
        monthYearLabel: { month: 'short', year: 'numeric' },
        dateA11yLabel: { day: 'numeric', month: 'long', year: 'numeric' },
        monthYearA11yLabel: { month: 'long', year: 'numeric' },
    },
};

export const IRISH_DATE_PROVIDERS = [
    { provide: DateAdapter, useClass: IrishNativeDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: IRISH_DATE_TIME_FORMATS },
];
